import { memo, useCallback, useEffect, useMemo, useState } from "react";

import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import { Button } from "ui/components/Button/Button";
import { FileDropZone } from "ui/components/FileUpload/FileDropZone";
import { FileExtended } from "ui/components/FileUpload/FileDropZone/types";
import FileUploadList from "ui/components/FileUpload/FileUploadList/FileUploadList";
import { FleetDocumentUpload } from "services/api/fleet/vehicles/documents/types";

interface VehicleImportFileModalProps {
  show: boolean;
  onClose: () => void;
  onSelect: (files: FileExtended[]) => void;
}

/** Zaseban modal za odabir XLSX datoteke (dropzone se ne stavlja direktno na ekran). */
const VehicleImportFileModal: React.FC<VehicleImportFileModalProps> = (props) => {
  const { show, onClose, onSelect } = props;

  const [files, setFiles] = useState<FileExtended[]>([]);

  useEffect(() => {
    if (show) {
      setFiles([]);
    }
  }, [show]);

  const handleFileChange = useCallback((newFiles: File[]) => {
    const extended: FileExtended[] = newFiles.map((file, index) => {
      const item = file as FileExtended;
      item.id = `${Date.now()}-${index}`;
      item.isNew = true;
      item.url = URL.createObjectURL(file);
      return item;
    });
    // Import prima jednu datoteku.
    setFiles(extended.slice(0, 1));
  }, []);

  const handleDeleteById = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const uploadFiles: FleetDocumentUpload[] = useMemo(
    () =>
      files.map((f) => ({
        id: f.id,
        originalFileName: f.name,
        fileExtension: f.name.split(".").pop() ?? "",
        fileSizeKb: Math.round((f.size || 0) / 1024),
        contentType: f.type || "",
        created: new Date(),
        url: f.url ?? "",
        name: f.name,
      })),
    [files]
  );

  const handleConfirm = useCallback(() => {
    if (files.length === 0) {
      return;
    }
    onSelect(files);
    onClose();
  }, [files, onSelect, onClose]);

  return (
    <Modal show={show} position="centered" size="small" onHide={onClose}>
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title="Učitaj XLSX datoteku"
        onHide={onClose}
      />
      <ModalBody>
        <div className="px-4 pt-5 pb-8 space-y-4">
          <FileDropZone
            onChange={handleFileChange}
            onDelete={() => {}}
            files={[]}
          />
          {uploadFiles.length > 0 && (
            <FileUploadList
              files={uploadFiles}
              onDelete={handleDeleteById}
              deleteTitle="Brisanje datoteke?"
              deleteMessage="Jeste li sigurni da želite obrisati datoteku?"
            />
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
          <Button variant="outlined" onClick={onClose}>
            Odustani
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={files.length === 0}
          >
            Odaberi
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default memo(VehicleImportFileModal);
